import { useEffect, useState } from "react";
import { Alert, Col, Nav, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import CampaignAndSurveyParamsForm from "../components/tabs/CampaignAndSurveyParamsForm";
import { getGeneralParams, updateGeneralParams } from "../api/generalParamsAPI";
import { getCurrentClient } from "../../../auth/api/getCurrentClient";
import { useDispatch, useSelector } from "react-redux";
import { clearErrors, setError, setGeneralParamsForm } from "../../../../redux/clientParamsSlice";
import { ClientGeneralParams } from "../types";
import { RootState } from "../../../../redux/store";
import { getClientParamsSchema } from "../schema/clientParamsSchema";
import GeneralParamsSectionForm from "../components/tabs/GeneralParamsSectionForm";
import DemandModuleParamsForm from "../components/tabs/DemandModuleParamsForm";
import PromptingParamsForm from "../components/tabs/PromptingParamsForm";
import { convertFileToBase64 } from "../../../../utils/formatters/FileFormatter";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";
import { useUser } from "../../../../utils/auth";

export default function GeneralParamsForm() {
    const { t } = useTranslation(['clientParams', 'common']);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { userInfo, setUserInfo } = useUser();
    const clientParamsForm = useSelector((state: RootState) => state.clientParamsForm.data);
    
    const [tab, setTab] = useState("general");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [logoResetTrigger, setLogoResetTrigger] = useState(0);
    const {
        showModal,
        modalData,
        openModal,
        closeModal,
        errorModal,
    } = useModal<null>();

    useEffect(() => {
        setIsLoading(true);
        setErrorMessage("");

        getGeneralParams()
            .then(data => dispatch(setGeneralParamsForm(data)))
            .catch((err: ApiErrorResponse) => {
                setErrorMessage(err.message || t("common:error"));
            })
            .finally(() => setIsLoading(false))
    }, [dispatch, t])

    useEffect(() => {
        if (!showSuccessMessage) return;

        const timer = setTimeout(() => {
            setShowSuccessMessage(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, [showSuccessMessage]);

    const updateField = (field: keyof ClientGeneralParams, value: any) => {
        dispatch(setGeneralParamsForm({ [field]: value }));
    };

    const handleRemoveLogoRequest = () => {
        openModal(
            null,
            t("clientParams:modal.modalDeleteFileLogoTitle"),
            t("clientParams:modal.modalDeleteFileLogoSubtitle"),
            "deleteWithoutInput",
            ""
        );
        
    };

    const handleRemoveLogoConfirm = () => {
        dispatch(setGeneralParamsForm({
            logoFile: null,
            logo: undefined,
            logoContentType: undefined,
            logoFileName: undefined,
        }));
        setLogoResetTrigger((prev) => prev + 1);
        closeModal();
    };

    const onSubmit = async () => {
        const schema = getClientParamsSchema(t);
        dispatch(clearErrors());

        try {
            await schema.validate(clientParamsForm, { abortEarly: false });
            
            await updateGeneralParams(clientParamsForm)
                .then(async () => {
                    setShowSuccessMessage(true);
                    try {
                        const updatedCurrentClient = await getCurrentClient();
                        queryClient.setQueryData(["currentClient"], updatedCurrentClient);
                    } finally {
                        queryClient.invalidateQueries({ queryKey: ["currentClient"] });
                    }

                    if (clientParamsForm.logoFile) {
                        const clientLogo = await convertFileToBase64(clientParamsForm.logoFile);
                        const clientLogoContentType = clientParamsForm.logoFile.type;

                        localStorage.setItem('clientLogo', clientLogo);
                        localStorage.setItem('clientLogoContentType', clientLogoContentType);

                        // Refresh the UserContext so the header logo updates instantly
                        setUserInfo({ ...userInfo, clientLogo, clientLogoContentType });

                    } else if (!clientParamsForm.logoFileName) {
                        localStorage.removeItem('clientLogo');
                        localStorage.removeItem('clientLogoContentType');
                        
                        setUserInfo({ ...userInfo, clientLogo: '', clientLogoContentType: '' });
                    }
                })
                .catch((err: ApiErrorResponse) => {
                    setErrorMessage(err.message || t("common:error"));
                });
        } catch (err: any) {
            err.inner.forEach((error: any) => {
                dispatch(setError({ 
                    field: error.path,
                    message: error.message 
                }));
            });
        }
    }
    
    return (
        <>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.errorModal}
                onSave={handleRemoveLogoConfirm}
                isLoading={false}
            />
            {showSuccessMessage && (
                <Alert variant="success" className="mb-3">
                    {t("clientParams:saveParamsSuccessfully")}
                </Alert>
            )}
            {errorMessage && (
                <Alert variant="danger" className="mb-3">
                    {errorMessage}
                </Alert>
            )}
            {isLoading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <Row className="mb-2">
                <Col xs={8} md={8}>
                    <h2>{t("clientParams:title")}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton onClick={onSubmit} saveLabel={t("common:save")} />
                </Col>
            </Row>
            <Nav variant="tabs" activeKey={tab}>
                <Nav.Item>
                    <Nav.Link
                        eventKey="general"
                        onClick={() => setTab("general")}
                        className={`survey-tab-link ${tab === "general" ? "active-tab" : ""}`}
                    >
                        {t("clientParams:tabs.generals")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link
                        eventKey="prompting"
                        onClick={() => setTab("prompting")}
                        className={`survey-tab-link ${tab === "prompting" ? "active-tab" : ""}`}
                    >
                        {t("clientParams:tabs.prompting")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link
                        eventKey="claim"
                        onClick={() => setTab("claim")}
                        className={`survey-tab-link ${tab === "claim" ? "active-tab" : ""}`}
                    >
                        {t("clientParams:tabs.claim")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link
                        eventKey="campaignAndSurvey"
                        onClick={() => setTab("campaignAndSurvey")}
                        className={`survey-tab-link ${tab === "campaignAndSurvey" ? "active-tab" : ""}`}
                    >
                        {t("clientParams:tabs.campaignAndSurvey")}
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            <div className="mt-3">
                {tab === "general" && <GeneralParamsSectionForm updateField={updateField} onRemoveLogo={handleRemoveLogoRequest} logoResetTrigger={logoResetTrigger} />}
                {tab === "prompting" && <PromptingParamsForm updateField={updateField} />}
                {tab === "claim" && <DemandModuleParamsForm updateField={updateField} />}
                {tab === "campaignAndSurvey" && <CampaignAndSurveyParamsForm updateField={updateField} />}
            </div>
        </>
    );
}



